import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ServerDoc } from '@panel/shared';
import { api } from '../lib/api.js';
import { useStore } from '../lib/store.js';
import { IconCheck, IconClose, IconPlus, IconWarning } from './Icons.js';
import CorePicker from './CorePicker.js';

interface Props {
  server: ServerDoc;
  canEdit: boolean;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Per-server settings. Currently surfaces wipe scheduling and the wipe-extras
 * file list — the other server fields (RCON, IP, directory) are not exposed
 * here because changing them mid-flight would desync the running process.
 */
export default function SettingsPanel({ server, canEdit }: Props) {
  const initial = useMemo(() => parseCron(server.Wipe_Cron ?? null), [server.Wipe_Cron]);
  const role = useStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const cores = useStore((s) => s.machines.get(server.Server_Machine)?.cpu.cores);

  const [autoWipe, setAutoWipe] = useState(server.Auto_Wipe);
  const [wipeDays, setWipeDays] = useState<number[]>(initial.days);
  const [wipeTime, setWipeTime] = useState<string>(initial.time);
  const [extras, setExtras] = useState<string[]>([...server.Wipe_Extras]);
  const [extraInput, setExtraInput] = useState('');
  const [affinity, setAffinity] = useState<string>(server.Server_AFFINITY ?? '');
  const [popBotEnabled, setPopBotEnabled] = useState(!!server.Pop_Bot_Enabled);
  const [popBotToken, setPopBotToken]     = useState(server.Pop_Bot_Token ?? '');
  const [popBotTpl, setPopBotTpl]         = useState(server.Pop_Bot_Status_Template ?? '({playersOnline}/{maxPlayers}) Online');
  const [popBotOff, setPopBotOff]         = useState(server.Pop_Bot_Offline_Status ?? '[ OFFLINE ]');
  const [showToken, setShowToken]         = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [showOffboard, setShowOffboard] = useState(false);

  useEffect(() => {
    setAutoWipe(server.Auto_Wipe);
    const parsed = parseCron(server.Wipe_Cron ?? null);
    setWipeDays(parsed.days);
    setWipeTime(parsed.time);
    setExtras([...server.Wipe_Extras]);
    setAffinity(server.Server_AFFINITY ?? '');
    setPopBotEnabled(!!server.Pop_Bot_Enabled);
    setPopBotToken(server.Pop_Bot_Token ?? '');
    setPopBotTpl(server.Pop_Bot_Status_Template ?? '({playersOnline}/{maxPlayers}) Online');
    setPopBotOff(server.Pop_Bot_Offline_Status ?? '[ OFFLINE ]');
  }, [server.Wipe_Cron, server.Auto_Wipe, server.Wipe_Extras, server.Server_AFFINITY,
      server.Pop_Bot_Enabled, server.Pop_Bot_Token, server.Pop_Bot_Status_Template, server.Pop_Bot_Offline_Status]);

  const dirty =
    autoWipe !== server.Auto_Wipe ||
    !sameSet(wipeDays, initial.days) ||
    wipeTime !== initial.time ||
    !sameArr(extras, server.Wipe_Extras) ||
    affinity !== (server.Server_AFFINITY ?? '') ||
    popBotEnabled !== !!server.Pop_Bot_Enabled ||
    popBotToken !== (server.Pop_Bot_Token ?? '') ||
    popBotTpl !== (server.Pop_Bot_Status_Template ?? '({playersOnline}/{maxPlayers}) Online') ||
    popBotOff !== (server.Pop_Bot_Offline_Status ?? '[ OFFLINE ]');

  const toggleDay = (d: number) => {
    setWipeDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b)));
  };

  const addExtra = () => {
    const v = extraInput.trim();
    if (!v || extras.includes(v)) return;
    setExtras([...extras, v]);
    setExtraInput('');
  };

  const removeExtra = (path: string) => setExtras(extras.filter((x) => x !== path));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        Auto_Wipe: autoWipe,
        Wipe_Extras: extras,
      };
      if (autoWipe) {
        if (wipeDays.length === 0) throw new Error('Pick at least one day for the wipe schedule');
        if (!/^\d{1,2}:\d{2}$/.test(wipeTime)) throw new Error('Time must be HH:MM');
        body.wipe_days = wipeDays;
        body.wipe_time = wipeTime;
      } else {
        body.Wipe_Cron = null;
      }
      const trimmedAffinity = affinity.trim();
      if (trimmedAffinity && !/^(0[xX][0-9a-fA-F]+|[0-9a-fA-F]+)$/.test(trimmedAffinity)) {
        throw new Error('Affinity must be empty, a decimal value (e.g. 65534), or hex (e.g. 0xFFFE)');
      }
      body.Server_AFFINITY = trimmedAffinity || null;
      body.Pop_Bot_Enabled = popBotEnabled;
      body.Pop_Bot_Token   = popBotToken.trim();
      body.Pop_Bot_Status_Template = popBotTpl;
      body.Pop_Bot_Offline_Status  = popBotOff;
      if (popBotEnabled && !popBotToken.trim()) {
        throw new Error('Pop bot token is required when the bot is enabled');
      }
      await api.patch(`/api/servers/${server.Server_ID}`, body);
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-5 space-y-6">
      <header className="flex items-center justify-between">
        <h3 className="font-semibold text-fg">Settings</h3>
        {savedAt && !dirty && (
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-status-online/10 text-status-online ring-1 ring-status-online/30 inline-flex items-center gap-1">
            <IconCheck className="w-3 h-3" /> Saved
          </span>
        )}
      </header>

      <fieldset className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-fg">Auto-wipe</div>
            <div className="text-xs text-fg-dim mt-0.5 max-w-md">
              First-Thursday-of-month is auto-skipped (Facepunch's force-wipe day).
            </div>
          </div>
          <Toggle checked={autoWipe} disabled={!canEdit} onChange={setAutoWipe} />
        </div>

        {autoWipe && (
          <div className="space-y-4 pl-1 pt-2 border-t border-bg-border">
            <div>
              <label className="label">Days</label>
              <div className="flex flex-wrap gap-1.5">
                {DAY_LABELS.map((label, idx) => {
                  const active = wipeDays.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggleDay(idx)}
                      className={`px-2.5 h-8 text-xs rounded-md border cursor-pointer transition-colors ${
                        active
                          ? 'bg-accent/15 border-accent/60 text-accent'
                          : 'bg-bg-raised border-bg-border text-fg-muted hover:text-fg hover:border-bg-ring'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="label">Time (server local)</label>
              <input
                type="time"
                className="input w-32 font-mono"
                value={wipeTime}
                disabled={!canEdit}
                onChange={(e) => setWipeTime(e.target.value)}
              />
            </div>

            {server.Wipe_Cron && (
              <div className="text-xs text-fg-dim">
                Current cron: <span className="font-mono text-fg-muted">{server.Wipe_Cron}</span>
              </div>
            )}
          </div>
        )}
      </fieldset>

      <div className="divider" />

      <fieldset className="space-y-3">
        <div>
          <div className="text-sm font-medium text-fg">CPU Affinity</div>
          <div className="text-xs text-fg-dim mt-0.5">
            Pick which logical cores RustDedicated.exe (and children) may use. Leave all selected for system default.
            Applied on next start.
          </div>
        </div>
        <CorePicker
          cores={cores}
          value={affinity}
          onChange={setAffinity}
          disabled={!canEdit}
        />
      </fieldset>

      <div className="divider" />

      <fieldset className="space-y-3">
        <div>
          <div className="text-sm font-medium text-fg">Wipe Extras</div>
          <div className="text-xs text-fg-dim mt-0.5">
            Files/folders (relative to server directory) deleted on every wipe in addition to map+save data.
            {' '}E.g. <code className="bg-bg-raised border border-bg-border px-1 rounded">oxide/data/AutoPurge.json</code>.
          </div>
        </div>

        <div className="space-y-1">
          {extras.length === 0 ? (
            <div className="text-xs text-fg-dim italic px-1">None</div>
          ) : (
            extras.map((path) => (
              <div key={path} className="flex items-center gap-2 surface px-3 h-9 group">
                <span className="font-mono text-xs flex-1 truncate" title={path}>{path}</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeExtra(path)}
                    className="btn-icon !w-6 !h-6 hover:!text-status-error opacity-50 group-hover:opacity-100"
                    aria-label={`Remove ${path}`}
                    title="Remove"
                  >
                    <IconClose className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {canEdit && (
          <div className="flex gap-2">
            <input
              className="input flex-1 font-mono text-xs"
              placeholder="oxide/data/SomePlugin.json"
              value={extraInput}
              onChange={(e) => setExtraInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExtra())}
            />
            <button type="button" className="btn-ghost btn-sm" onClick={addExtra} disabled={!extraInput.trim()}>
              <IconPlus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        )}
      </fieldset>

      <div className="divider" />

      <fieldset className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-fg flex items-center gap-2">
              Discord pop bot
              {popBotEnabled && popBotToken && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-status-online/10 text-status-online ring-1 ring-status-online/30">live</span>
              )}
            </div>
            <div className="text-xs text-fg-dim mt-0.5 max-w-md">
              A Discord bot whose status reflects this server's live player count. Token is stored on the Hub and never leaves it.
            </div>
          </div>
          <Toggle checked={popBotEnabled} disabled={!canEdit} onChange={setPopBotEnabled} />
        </div>

        {popBotEnabled && (
          <div className="space-y-3 pt-1">
            <div>
              <label className="label">Bot token</label>
              <div className="flex gap-2">
                <input
                  className="input font-mono text-xs flex-1"
                  type={showToken ? 'text' : 'password'}
                  value={popBotToken}
                  disabled={!canEdit}
                  onChange={(e) => setPopBotToken(e.target.value)}
                  placeholder="Discord bot token"
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setShowToken((v) => !v)}
                >
                  {showToken ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="text-xs text-fg-dim mt-1.5">
                Get one from the <a href="https://discord.com/developers/applications" target="_blank" rel="noreferrer" className="text-accent hover:underline">Discord Developer Portal</a>.
                The bot only needs to be in your guild — no privileged intents required.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">Status template</label>
                <input
                  className="input font-mono text-xs"
                  value={popBotTpl}
                  disabled={!canEdit}
                  onChange={(e) => setPopBotTpl(e.target.value)}
                  maxLength={120}
                />
                <p className="text-[11px] text-fg-dim mt-1">
                  Placeholders: <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{playersOnline}'}</code>{' '}
                  <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{maxPlayers}'}</code>{' '}
                  <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{joiningPlayers}'}</code>{' '}
                  <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{queuedPlayers}'}</code>
                </p>
              </div>
              <div>
                <label className="label">Offline status</label>
                <input
                  className="input font-mono text-xs"
                  value={popBotOff}
                  disabled={!canEdit}
                  onChange={(e) => setPopBotOff(e.target.value)}
                  maxLength={120}
                />
                <p className="text-[11px] text-fg-dim mt-1">Shown when the underlying RCON server is offline.</p>
              </div>
            </div>
          </div>
        )}
      </fieldset>

      {error && (
        <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
          {error}
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end pt-3 border-t border-bg-border">
          <button
            className="btn-primary"
            disabled={!dirty || saving}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}

      {canEdit && (
        <div className="mt-4 pt-5 border-t border-status-error/25">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-medium text-status-error flex items-center gap-2">
                <IconWarning className="w-4 h-4" /> Danger zone
              </div>
              <p className="text-xs text-fg-muted mt-1 max-w-md">
                Offboard this server. Stops the running process, removes the
                Hub record, and (optionally) wipes its directory on the agent.
              </p>
            </div>
            <button
              type="button"
              className="btn-danger btn-sm"
              onClick={() => setShowOffboard(true)}
            >
              Remove server…
            </button>
          </div>
        </div>
      )}

      {showOffboard && (
        <OffboardModal
          server={server}
          isAdmin={isAdmin}
          onCancel={() => setShowOffboard(false)}
        />
      )}
    </div>
  );
}

function OffboardModal({ server, isAdmin, onCancel }: { server: ServerDoc; isAdmin: boolean; onCancel: () => void }) {
  const nav = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [stopFirst, setStopFirst] = useState(true);
  const [finalBackup, setFinalBackup] = useState(true);
  const [deleteFiles, setDeleteFiles] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<Array<{ step: string; ok: boolean; message?: string }> | null>(null);
  const matches = confirmation.trim() === server.Server_Name;

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.delete<{ ok: true; steps: Array<{ step: string; ok: boolean; message?: string }> }>(
        `/api/servers/${server.Server_ID}`,
        { stop_first: stopFirst, final_backup: finalBackup, delete_files: deleteFiles },
      );
      setSteps(res.steps);
      setTimeout(() => nav('/'), 1500);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div
        className="card p-6 w-full max-w-md shadow-modal animate-slide-up !border-status-error/30"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="offboard-title"
      >
        <h3 id="offboard-title" className="text-lg font-semibold mb-2 flex items-center gap-2">
          <IconWarning className="w-5 h-5 text-status-error" />
          <span>Remove <span className="text-fg font-mono">{server.Server_Name}</span></span>
        </h3>
        <p className="text-sm text-fg-muted mb-4">
          This is permanent. The server doc is removed from the Hub and the
          agent stops the running process. Backups in R2 are <strong className="text-fg">not</strong> deleted unless you do so separately.
        </p>

        <div className="space-y-2 text-sm">
          <OptionToggle checked={stopFirst} onChange={setStopFirst} disabled={busy}>
            <div className="font-medium text-fg">Stop the server first</div>
            <div className="text-xs text-fg-dim">Soft-stop with grace before removal.</div>
          </OptionToggle>
          <OptionToggle checked={finalBackup} onChange={setFinalBackup} disabled={busy}>
            <div className="font-medium text-fg">Trigger a final backup</div>
            <div className="text-xs text-fg-dim">Snapshots the server one last time to R2.</div>
          </OptionToggle>
          <OptionToggle
            checked={deleteFiles}
            onChange={setDeleteFiles}
            disabled={busy || !isAdmin}
            tone="danger"
          >
            <div className="font-medium text-status-error">Delete files on disk</div>
            <div className="text-xs text-fg-dim">
              Recursively removes the server directory on the agent.
              {!isAdmin && ' Admin only.'}
            </div>
          </OptionToggle>
        </div>

        <div className="mt-4">
          <label className="label">
            Type <span className="font-mono text-fg">{server.Server_Name}</span> to confirm
          </label>
          <input
            className="input font-mono"
            autoFocus
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={server.Server_Name}
            disabled={busy}
          />
        </div>

        {error && (
          <div className="mt-3 text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
            {error}
          </div>
        )}

        {steps && (
          <ul className="mt-3 space-y-1 text-xs">
            {steps.map((s) => (
              <li key={s.step} className={s.ok ? 'text-status-online' : 'text-status-warn'}>
                {s.ok ? '✓' : '⚠'} {s.step}{s.message ? ` — ${s.message}` : ''}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn-danger" disabled={!matches || busy} onClick={submit}>
            {busy ? 'Removing…' : 'Remove server'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OptionToggle({
  checked, onChange, disabled, tone, children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  tone?: 'danger';
  children: React.ReactNode;
}) {
  const activeBg = tone === 'danger' ? 'peer-checked:bg-status-error' : 'peer-checked:bg-accent';
  return (
    <label className={`flex items-start gap-3 px-3 py-2 rounded-lg border bg-bg-raised border-bg-border ${disabled ? 'opacity-50' : 'cursor-pointer hover:border-bg-ring'}`}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={`shrink-0 mt-0.5 w-9 h-5 rounded-full bg-bg-border ${activeBg} transition-colors relative`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-fg rounded-full transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </span>
      <span className="flex-1 min-w-0">{children}</span>
    </label>
  );
}

function Toggle({ checked, disabled, onChange }: { checked: boolean; disabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center cursor-pointer shrink-0">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="w-10 h-5.5 bg-bg-border rounded-full peer-checked:bg-accent peer-disabled:opacity-50 transition-colors relative" style={{ height: '1.375rem' }}>
        <span
          className={`absolute top-0.5 left-0.5 w-[1.125rem] h-[1.125rem] bg-fg rounded-full shadow-sm transition-transform duration-150 ${
            checked ? 'translate-x-[1.125rem]' : ''
          }`}
        />
      </div>
    </label>
  );
}

function parseCron(cron: string | null): { days: number[]; time: string } {
  if (!cron) return { days: [], time: '06:00' };
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 6) return { days: [], time: '06:00' };
  const mm = parseInt(parts[1], 10);
  const hh = parseInt(parts[2], 10);
  const dayList = parts[5];
  const days = dayList === '*'
    ? [0, 1, 2, 3, 4, 5, 6]
    : dayList.split(',').map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n));
  const time = `${String(hh || 0).padStart(2, '0')}:${String(mm || 0).padStart(2, '0')}`;
  return { days, time };
}

function sameSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function sameArr(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}
